// What this browser has noticed about its own visitor, and when that is worth
// telling the server about.
//
// PURE: no storage, no clock, no network. `use-engagement.ts` is the shell.
//
// THIS IS A TRIGGER, NOT AN AUTHORITY. Nothing decided here is believed by the
// server — `convex/engagement.ts` re-derives every tier it can from rows a
// browser cannot forge, and the one claim it does accept (`explored`) buys the
// bottom rung. The job of this file is only to decide WHEN to ask, cheaply and
// locally, so that a visitor who is going to bounce never costs a round trip.
//
// The asymmetry that shapes it: asking too often is a wasted read, asking too
// rarely silently loses a conversion signal. So the heuristics below lean
// generous, and the server's answer is what gets stored.

import type { StoredEngagement } from "@/lib/prefs";

/**
 * Distinct routes that count as having looked around.
 *
 * THREE, AND IT IS A GUESS. "Any N distinct routes" is the weakest of the
 * signals by design — it may well turn out to predict nothing, and the
 * calibration is what will say so. Deliberately not tuned to intent (the
 * subscribe page, the library) because a signal defined by where we already
 * think value is cannot tell us anything we did not already assume.
 */
export const EXPLORED_ROUTE_THRESHOLD = 3;

/** How long a gap makes it worth re-asking the server. Mirrors `RETURN_GAP_MS`
 *  in `convex/engagement.ts`: it is the same "came back" the server measures,
 *  and the client's only job is to make sure someone is there to notice. */
export const REASK_AFTER_MS = 6 * 60 * 60 * 1000;

/**
 * How long a REFUSED candidate waits before asking again.
 *
 * The refused candidate is the common case, not the odd one: opening a chapter
 * makes the optimistic candidate 2 (`localCandidateTier`), the server verifies
 * completion and answers 1, and the candidate stays above the answer for as
 * long as the chapter stays unfinished. Ungated, that condition re-fired the
 * mutation on EVERY navigation for the browser's lifetime — the exact
 * per-navigation chatter the "handful of calls per visitor lifetime" design
 * exists to avoid.
 *
 * Fifteen minutes, not `REASK_AFTER_MS`'s six hours, because this retry is how
 * a finished chapter gets noticed: reading one takes longer than this, so the
 * first navigation after really finishing re-asks — while a reader bouncing
 * between routes costs at most four calls an hour instead of one per click.
 */
export const RETRY_UNCONFIRMED_MS = 15 * 60 * 1000;

/**
 * The refused-candidate clock when the page is about to disappear.
 *
 * The fifteen-minute retry above assumes there will BE a later navigation to
 * carry it — and for the single-page ad landing there never is: a visitor
 * lands on a chapter (one ask, refused, `askedTier` 2), reads it to the end,
 * and closes the tab. Every event after the landing happens between
 * navigations, so the only report this visitor ever makes is the one from
 * before they read anything. That was most of the funnel: on the first
 * campaign days, 7 of the 11 finishers reported nothing.
 *
 * `visibilitychange → hidden` is the one moment such a session is guaranteed
 * to have (it is the same event the reading telemetry flushes on), so the
 * hide path asks on a much shorter clock. A minute, not zero, because hides
 * are frequent on mobile (every app switch) and the answer cannot have
 * changed faster than a paragraph can be read.
 */
export const HIDDEN_RETRY_MS = 60 * 1000;

/** How often the open-tab timer re-evaluates `shouldReport`. Coarse — the
 *  timer exists only so a reader who finishes a chapter and then sits (no
 *  navigation, no hide) is eventually asked about on the fifteen-minute
 *  clock rather than never. */
export const REPORT_TICK_MS = 60 * 1000;

/** Routes kept. Past the threshold the exact set stops mattering, so this only
 *  bounds a long-lived browser's stored blob. */
const MAX_TRACKED_ROUTES = 12;

export function emptyEngagement(): StoredEngagement {
  return { routes: [], reportedTier: 0, askedTier: 0, lastReportAt: 0 };
}

/** The highest tier ever asked of the server. Absent on blobs written before
 *  the field existed, where the only asks that can have happened are the ones
 *  the server acknowledged — so `reportedTier` is the honest floor. */
export function askedTierOf(state: StoredEngagement): number {
  return Math.max(state.reportedTier, state.askedTier ?? state.reportedTier);
}

/**
 * Trim a stored blob back inside its caps.
 *
 * Applied on WRITE rather than on read, so a hand-edited or corrupted value is
 * repaired in place instead of rejected. Rejecting would reset `reportedTier`,
 * and a visitor whose high-water mark went back to zero would re-report tiers
 * that have already been sent to Google — which the delta model reads as new
 * value and adds on top.
 */
function trim(state: StoredEngagement): StoredEngagement {
  const reportedTier = Math.max(0, Math.floor(state.reportedTier));
  return {
    // The OLDEST are kept, not the newest: the threshold is about breadth, and
    // a visitor who has already passed it should not be able to drop back
    // below it by continuing to browse.
    routes: state.routes.slice(0, MAX_TRACKED_ROUTES),
    reportedTier,
    // Never below `reportedTier`: an acknowledged tier was by definition asked.
    askedTier: Math.max(reportedTier, Math.floor(state.askedTier ?? 0)),
    lastReportAt: Math.max(0, state.lastReportAt),
  };
}

/** Note a route. Distinct paths only — a reader moving back and forth between
 *  two chapters has not explored three things. */
export function withRoute(
  state: StoredEngagement,
  path: string,
): StoredEngagement {
  if (state.routes.includes(path)) return state;
  return trim({ ...state, routes: [...state.routes, path] });
}

/** Note that the server was just asked about `candidate`, BEFORE the call —
 *  so a reply that never arrives, or a StrictMode double-mount, cannot leave
 *  the browser asking the same question again on the very next render. */
export function withAsked(
  state: StoredEngagement,
  candidate: number,
  now: number,
): StoredEngagement {
  return trim({
    ...state,
    askedTier: Math.max(askedTierOf(state), candidate),
    lastReportAt: now,
  });
}

/** Note the server's answer. `reportedTier` only ever climbs — see `trim`. */
export function withServerTier(
  state: StoredEngagement,
  tier: number,
  now: number,
): StoredEngagement {
  return trim({
    ...state,
    reportedTier: Math.max(state.reportedTier, tier),
    lastReportAt: now,
  });
}

/**
 * The highest tier this browser has local reason to think the server MIGHT
 * accept.
 *
 * DELIBERATELY OPTIMISTIC. Opening a chapter is treated as possibly having
 * finished it, because the browser cannot tell — completion is measured
 * against the server's own block count, and asking is the only way to find
 * out. The cost of asking and being told "no" is one cheap read: the server
 * skips the expensive verification for any unit the reading row itself says
 * was left unfinished.
 *
 * The alternative — tracking completion locally and only asking when sure —
 * duplicates the completion rule in a second place, where it would drift from
 * the server's copy silently and in the direction of under-reporting.
 */
export function localCandidateTier(
  state: StoredEngagement,
  signedIn: boolean,
): number {
  if (signedIn) return 4;
  const readRoutes = state.routes.filter((path) =>
    path.startsWith("/read/"),
  ).length;
  if (readRoutes >= 2) return 3;
  if (readRoutes >= 1) return 2;
  if (state.routes.length >= EXPLORED_ROUTE_THRESHOLD) return 1;
  return 0;
}

/**
 * Whether to call the server now.
 *
 * Three reasons, each with its own clock:
 *
 * 1. NEW EVIDENCE — the candidate reaches a rung this browser has never asked
 *    about (`askedTier`). Fires immediately: a second read route, a sign-in.
 * 2. A REFUSED CANDIDATE — asked before, the server stored less, and nothing
 *    locally has changed. Retries on `RETRY_UNCONFIRMED_MS`, because the
 *    thing that would change the answer (finishing the chapter) happens
 *    between navigations, not at them — which is also why this rule is
 *    evaluated from a timer and from `visibilitychange`, not only from route
 *    changes (`use-engagement.ts`). Gating on `askedTier` rather than firing
 *    whenever `candidate > reportedTier` is what keeps this from being a
 *    mutation on every navigation forever — the contract the server's
 *    "CALLED RARELY BY DESIGN" comment depends on.
 * 3. THE LONG RE-ASK — the visitor may have crossed a tier only the SERVER can
 *    see (came back after a gap, read in another tab). Without it, "returned
 *    after several hours" would only ever be recorded for visitors who
 *    happened to do something else as well.
 */
export function shouldReport(
  state: StoredEngagement,
  candidate: number,
  now: number,
  /** The refused-candidate clock. `HIDDEN_RETRY_MS` on the hide path — see
   *  its comment; every other caller takes the default. */
  retryMs: number = RETRY_UNCONFIRMED_MS,
): boolean {
  if (candidate === 0) return false;
  if (candidate > state.reportedTier) {
    return (
      candidate > askedTierOf(state) || now - state.lastReportAt >= retryMs
    );
  }
  return state.reportedTier > 0 && now - state.lastReportAt >= REASK_AFTER_MS;
}

/** Whether the visitor has browsed enough distinct routes to claim it. The one
 *  thing the server takes on trust, so it is computed in exactly one place. */
export function hasExplored(state: StoredEngagement): boolean {
  return state.routes.length >= EXPLORED_ROUTE_THRESHOLD;
}
